/**
 * Dashboard del vecino — replica `calbucura-design-handoff/reference-
 * pages/01-vecino-dashboard.html` con datos reales del backend.
 *
 * Sprint 11 v2 S11-V2-05. Estructura:
 * - Greeting split (HOLA {NOMBRE} sin coma) con alerta a la derecha
 *   cuando la casa no está al día (`aviso_morosidad`).
 * - 3 KPI cards (Saldo pendiente warning, Próxima cuota, Cuotas pagadas).
 * - 2 cards lado a lado: Últimos movimientos + Mis datos.
 * - Sección Documentos internos (4 DocCards en grid 2x2 con datos mock).
 * - Sección Avisos (3 Notices con datos mock).
 *
 * Datos reales del endpoint `GET /lotes/{lote_id}/estado-cuenta`
 * (enriquecido en Sprint 10 S10-05). Los modelos de documentos y
 * avisos no existen aún; datos mock en `src/data/mocks-dashboard.ts`
 * (anchor `DOCUMENTOS_AVISOS_MODELS` para Sprint 12+).
 *
 * El aviso de morosidad (2026-10-07, convención #20) lo arma el backend
 * (`aviso_morosidad`): el mismo texto que la campana, con la deuda total y
 * el nivel del motor. Antes se derivaba aquí desde la prorrata
 * (`atraso_mensual`), que es interna de tesorería.
 *
 * Sprint 14 S14-07: refactorizado al design system. Email y celular del
 * card "Mis datos" pasan a `<InfoValue mask="email|celular" />`
 * (patrón canónico definido en S14-06b). Section headers de Documentos
 * internos y Avisos pasan a `<SectionHead actions={<Link/>} />`
 * preservando la semántica del Link (no botón).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../lib/api/client';
import { casasDelVecino } from '../../lib/casas-vecino';
import { getEstadoCuenta, listarVehiculosDeLote } from '../../lib/api/lotes';
import { listarDocumentosVecino } from '../../lib/api/documentos';
import { listarAvisosVecino } from '../../lib/api/avisos';
import {
  listarResidentesDeLote,
  type ResidenteConPersonaRead,
} from '../../lib/api/residentes';
import type {
  AvisoMorosidadPortal,
  AvisoRead,
  DocumentoRead,
  EstadoCuentaResponse,
  VehiculoRead,
} from '../../lib/api/types';
import {
  iconVariantPorTipo,
  metaDocumento,
  timeAviso,
} from '../../lib/dashboard-helpers';
import { useAuth } from '../../lib/auth/AuthContext';
import { formatCLP, formatFecha } from '../../lib/format';
import { formatearUltimoAcceso } from '../../lib/tiempo';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DataList, DataRow } from '../../components/ui/DataList';
import { DatosCuentaModal } from '../../components/ui/DatosCuentaModal';
import { DocCard } from '../../components/ui/DocCard';
import { Greeting, type AlertaSeveridad } from '../../components/ui/Greeting';
import { InfoValue } from '../../components/ui/InfoValue';
import { Notice, Notices } from '../../components/ui/Notice';
import { Row } from '../../components/ui/Row';
import { SectionHead } from '../../components/ui/Section';
import { StatCard } from '../../components/ui/StatCard';
import { SaldoAFavorBanner } from '../../components/ui/SaldoAFavorBanner';
import { DOC_ICONS_BY_VARIANT } from '../../data/doc-icons';

const NOMBRES_MES_ES_CORTO = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

const NOMBRES_MES_ES_LARGO = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/**
 * Devuelve "—" (vacío) o el número como string. Sprint 14 S14-Dash-Counts:
 * el Dashboard muestra solo la cantidad en el `DataRow`; la unidad
 * (vehículos/residentes) ya está en el label izquierdo. El detalle
 * (patentes, nombres) vive en `/vecino/mis-datos`.
 */
function contadorLegible(count: number): string {
  if (count === 0) return '—';
  return String(count);
}

function primerNombre(nombreCompleto: string): string {
  const t = (nombreCompleto || '').trim();
  if (!t) return '';
  const p = t.split(/\s+/)[0]!;
  return p[0]!.toUpperCase() + p.slice(1).toLowerCase();
}

function fechaCorta(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return formatFecha(iso);
  const [, , mm, dd] = m;
  const idx = parseInt(mm, 10) - 1;
  return `${parseInt(dd, 10)} ${NOMBRES_MES_ES_CORTO[idx] ?? mm}`;
}

function fechaLargaEs(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return formatFecha(iso);
  const [, anio, mm, dd] = m;
  const idx = parseInt(mm, 10) - 1;
  return `${parseInt(dd, 10)} ${NOMBRES_MES_ES_LARGO[idx] ?? mm} ${anio}`;
}

function periodoLegible(periodo: string): string {
  // YYYY-S1 / YYYY-S2 -> "ene 2026" / "jul 2026" (mes inicial del semestre).
  const m = /^(\d{4})-S(1|2)$/.exec(periodo);
  if (!m) return periodo;
  const [, anio, sem] = m;
  return sem === '1' ? `enero ${anio}` : `julio ${anio}`;
}

function semestreLegible(periodo: string): string {
  // YYYY-S1 / YYYY-S2 -> "1er semestre 2026" / "2do semestre 2026". Se usa
  // en la tarjeta "Próxima cuota": junto al vencimiento (ej. 31-dic) la
  // etiqueta del semestre es más clara que el mes inicial ("julio 2026"),
  // que se leía como si la cuota fuera de julio.
  const m = /^(\d{4})-S(1|2)$/.exec(periodo);
  if (!m) return periodo;
  const [, anio, sem] = m;
  return sem === '1' ? `1er semestre ${anio}` : `2do semestre ${anio}`;
}

/** Top-N movimientos para "Últimos movimientos": solo pagos aplicados
 *  con sus distribuciones, ordenados por fecha de transferencia
 *  descendente.
 *
 *  S15-Vec.1: el feed refleja únicamente movimientos reales del vecino.
 *  Las cuotas generadas o por vencer son control interno y se ven en
 *  Mi Cuenta; nunca como "movimiento". El título de cada fila usa la
 *  `descripcion` de la DistribucionPago (texto específico por pago,
 *  patrón S15-03c) para que pagos contra la misma cuota semestral
 *  muestren su detalle (cuota ene-jun, multa XXX, etc.).
 */
function ultimosMovimientos(estado: EstadoCuentaResponse): UltimoMovimiento[] {
  const items: UltimoMovimiento[] = [];

  for (const c of estado.cuotas) {
    for (const pago of c.pagos_aplicados ?? []) {
      items.push({
        key: `p-${pago.pago_id}-${c.cuota_id}`,
        title: pago.descripcion || `Pago aplicado a cuota ${periodoLegible(c.periodo)}`,
        sub: fechaCorta(pago.fecha_transferencia),
        amount: `+${formatCLP(pago.monto_aplicado)}`,
        amountVariant: 'in',
        orderKey: pago.fecha_transferencia,
      });
    }
  }

  items.sort((a, b) => b.orderKey.localeCompare(a.orderKey));
  return items.slice(0, 4);
}

interface UltimoMovimiento {
  key: string;
  title: string;
  sub: string;
  amount: string;
  amountVariant: 'in' | 'out' | 'overdue';
  orderKey: string;
}

/** Alerta del Greeting a partir del aviso del backend. Deudor y moroso van
 *  en amarillo; suspendible y suspendida, en rojo. */
function alertaDesdeAviso(
  aviso: AvisoMorosidadPortal | null,
):
  | { titulo: string; mensaje: string; severidad: AlertaSeveridad }
  | undefined {
  if (!aviso) return undefined;
  const severidad: AlertaSeveridad =
    aviso.tipo === 'suspendible' || aviso.tipo === 'suspendida'
      ? 'danger'
      : 'warning';
  return { titulo: aviso.titulo, mensaje: aviso.mensaje, severidad };
}

/* ===== Icons SVG inline ===== */

function IconCard() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M3 7 H21 V19 H3 Z" />
      <path d="M3 11 H21" />
    </svg>
  );
}

/* ===== Subcomponentes ===== */

function SkeletonDashboard() {
  return (
    <div className="px-4 md:px-8 py-6 space-y-6" aria-busy="true">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-bg-sunken rounded-md p-5">
            <div className="h-3 w-24 bg-white/10 rounded mb-3" />
            <div className="h-7 w-32 bg-white/15 rounded mb-3" />
            <div className="h-3 w-40 bg-white/10 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface VecinoDashboardProps {
  me: NonNullable<ReturnType<typeof useAuth>['me']>;
  lastSignInAt: string | null;
  estado: EstadoCuentaResponse;
  vehiculos: VehiculoRead[];
  residentes: ResidenteConPersonaRead[];
  documentos: DocumentoRead[];
  avisos: AvisoRead[];
  tenenciasMultiples: boolean;
  loteSeleccionado: string;
  onLoteChange: (id: string) => void;
  tenencias: { lote_id: string; lote_identificador: string }[];
}

function VecinoDashboard({
  me,
  lastSignInAt,
  estado,
  vehiculos,
  residentes,
  documentos,
  avisos,
  tenenciasMultiples,
  loteSeleccionado,
  onLoteChange,
  tenencias,
}: VecinoDashboardProps) {
  const alerta = alertaDesdeAviso(estado.aviso_morosidad);
  const movimientos = ultimosMovimientos(estado);

  // Sprint 15 S15-01: modal "Datos para transferir" se abre desde el
  // botón "Cómo pagar" de la card "Últimos movimientos" cuando hay saldo
  // pendiente. State local del dashboard porque el botón vive aquí.
  const [modalDatosAbierto, setModalDatosAbierto] = useState(false);

  const eyebrow = `HOLA ${primerNombre(me.persona.nombre_completo).toUpperCase()}`;
  const meta = lastSignInAt
    ? `Último acceso: ${formatearUltimoAcceso(lastSignInAt)}`
    : undefined;

  return (
    <>
      <Greeting eyebrow={eyebrow} title="Resumen de tu cuenta" meta={meta} alerta={alerta} />

      {tenenciasMultiples && (
        <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-4 md:px-8 py-3 text-sm text-text-secondary">
          Casa:{' '}
          <select
            value={loteSeleccionado}
            onChange={(e) => onLoteChange(e.target.value)}
            className="ml-2 bg-bg-card border-[0.5px] border-white/15 rounded-sm px-2 py-1 text-text-primary"
          >
            {tenencias.map((t) => (
              <option key={t.lote_id} value={t.lote_id}>
                {t.lote_identificador}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 3 KPI cards */}
      <section className="px-4 md:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Sprint 17 S17-Z5: saldo_semestre_actual = lo que efectivamente
              le falta al vecino del semestre en curso + arrastre. El campo
              saldo_total_pendiente del endpoint solo incluye cuotas
              vencidas, lo que oculta el saldo del semestre antes del 30/jun. */}
          <StatCard
            label="Saldo pendiente"
            value={formatCLP(estado.saldo_semestre_actual)}
            meta={
              estado.saldo_semestre_actual === 0
                ? 'Estás al día'
                : 'Cuota semestral'
            }
            variant="warning"
          />

          <StatCard
            label="Próxima cuota"
            value={
              estado.proxima_cuota_a_vencer
                ? fechaCorta(estado.proxima_cuota_a_vencer.vence_el)
                : '—'
            }
            meta={
              estado.proxima_cuota_a_vencer
                ? `${formatCLP(estado.proxima_cuota_a_vencer.saldo_pendiente)} · ${semestreLegible(
                    estado.proxima_cuota_a_vencer.periodo,
                  )}`
                : 'Sin cuotas futuras'
            }
          />

          <StatCard
            label="Cuotas pagadas"
            value={estado.meses_cubiertos_anio_actual.toString()}
            valueFaded="de 12"
            meta={`Año ${new Date().getFullYear()}`}
          />
        </div>
        <SaldoAFavorBanner
          monto={estado.saldo_a_favor_actual}
          className="mt-3"
        />
      </section>

      {/* 2 cards: Últimos movimientos + Mis datos */}
      <section className="px-4 md:px-8 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card
            title="Últimos movimientos"
            actionRight={
              <div className="flex items-center gap-3">
                <Link
                  to="/vecino/estado-cuenta"
                  className="text-sm text-orange font-medium hover:underline"
                >
                  Ver detalle
                </Link>
                {estado.saldo_semestre_actual > 0 && (
                  <Button
                    variant="cream"
                    size="sm"
                    onClick={() => setModalDatosAbierto(true)}
                  >
                    <IconCard />
                    Cómo pagar
                  </Button>
                )}
              </div>
            }
          >
            <div className="flex flex-col gap-2">
              {movimientos.length === 0 ? (
                <p className="text-sm text-text-muted">Sin movimientos recientes.</p>
              ) : (
                movimientos.map((m, i) => (
                  <div key={m.key}>
                    <Row title={m.title} sub={m.sub} amount={m.amount} amountVariant={m.amountVariant} />
                    {i < movimientos.length - 1 && <hr className="border-white/[0.08] my-1" />}
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card
            title="Mis datos"
            actionRight={
              <Link
                to="/vecino/mis-datos"
                className="text-sm text-orange font-medium hover:underline"
              >
                Editar
              </Link>
            }
          >
            <DataList>
              <DataRow label="Nombre" value={primerNombre(me.persona.nombre_completo) + ' ' + (me.persona.nombre_completo.split(/\s+/)[1]?.[0] ?? '') + '.'} />
              <DataRow label="Casa" value={estado.identificador} />
              <DataRow
                label="Teléfono"
                value={<InfoValue value={me.persona.celular} mask="celular" />}
              />
              <DataRow
                label="Email"
                value={<InfoValue value={me.persona.email} mask="email" />}
              />
              <DataRow label="Vehículos" value={contadorLegible(vehiculos.length)} />
              <DataRow label="Residentes" value={contadorLegible(residentes.length)} />
            </DataList>
          </Card>
        </div>
      </section>

      {/* Documentos internos */}
      <section className="px-4 md:px-8 pb-6">
        <SectionHead
          title="Documentos internos"
          actions={
            <Link
              to="/vecino/documentos"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-base"
            >
              Ver todos
            </Link>
          }
        />
        {documentos.length === 0 ? (
          <p className="text-sm text-text-muted">Sin documentos publicados.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {documentos.slice(0, 4).map((doc) => {
              const variant = iconVariantPorTipo(doc.tipo);
              return (
                <a
                  key={doc.id}
                  href={doc.url_externa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-90"
                >
                  <DocCard
                    icon={DOC_ICONS_BY_VARIANT[variant]!}
                    iconVariant={variant}
                    title={doc.titulo}
                    meta={metaDocumento(doc)}
                  />
                </a>
              );
            })}
          </div>
        )}
      </section>

      {/* Avisos */}
      <section className="px-4 md:px-8 pb-8">
        <SectionHead
          title="Avisos"
          actions={
            <Link
              to="/vecino/avisos"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-base"
            >
              Ver todos
            </Link>
          }
        />
        {avisos.length === 0 ? (
          <p className="text-sm text-text-muted">Sin avisos del barrio.</p>
        ) : (
          <Notices>
            {avisos.slice(0, 3).map((aviso) => (
              <Notice
                key={aviso.id}
                title={aviso.titulo}
                text={aviso.cuerpo}
                time={timeAviso(aviso)}
                unread={false}
              />
            ))}
          </Notices>
        )}
      </section>

      {estado.ultima_actividad && (
        <div className="px-4 md:px-8 pb-6 text-xs text-text-muted">
          Última transferencia aprobada: {fechaLargaEs(estado.ultima_actividad)}
        </div>
      )}

      {/* Sprint 15 S15-01: modal "Datos para transferir" disparado desde
          el botón "Cómo pagar" arriba. */}
      {modalDatosAbierto && (
        <DatosCuentaModal onClose={() => setModalDatosAbierto(false)} />
      )}
    </>
  );
}

/* ===== Componente principal ===== */

export function VecinoHome() {
  const { me, lastSignInAt } = useAuth();
  // Sprint 19 S19-C3: el aviso "configuración enviada a validación" pasó a ser
  // una NotificacionVecino (campanita, tipo onboarding_enviado) creada al
  // finalizar el wizard. Se eliminó el banner one-shot que vivía en
  // history.state (bug: sobrevivía al F5 y no se podía cerrar).
  // Casas del vecino para el selector: tenencias (titular) + casas donde es
  // autorizado de portal. Fuente única en casasDelVecino.
  const casas = casasDelVecino(me);

  const [loteSeleccionado, setLoteSeleccionado] = useState<string>(
    casas[0]?.lote_id ?? '',
  );
  const [estadoCuenta, setEstadoCuenta] = useState<EstadoCuentaResponse | null>(null);
  const [vehiculos, setVehiculos] = useState<VehiculoRead[]>([]);
  const [residentes, setResidentes] = useState<ResidenteConPersonaRead[]>([]);
  const [documentos, setDocumentos] = useState<DocumentoRead[]>([]);
  const [avisos, setAvisos] = useState<AvisoRead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loteSeleccionado) {
      setEstadoCuenta(null);
      setVehiculos([]);
      setResidentes([]);
      return;
    }
    let cancelado = false;
    setLoading(true);
    setError(null);
    getEstadoCuenta(loteSeleccionado)
      .then((data) => {
        if (!cancelado) setEstadoCuenta(data);
      })
      .catch((err) => {
        if (cancelado) return;
        const mensaje =
          err instanceof ApiError
            ? `No pudimos cargar el estado de cuenta (error ${err.status}).`
            : 'No pudimos cargar el estado de cuenta. Intenta recargar la página.';
        setError(mensaje);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    // Vehiculos: fetch best-effort. Si falla, queda lista vacia y el
    // resto del dashboard sigue funcionando (Sprint 12 S12-11).
    listarVehiculosDeLote(loteSeleccionado)
      .then((data) => {
        if (!cancelado) setVehiculos(data);
      })
      .catch(() => {
        if (!cancelado) setVehiculos([]);
      });
    // Residentes: fetch best-effort. Sprint 14 S14-Dash-Counts. El
    // endpoint por default filtra estados `pendiente_validacion` y
    // `aprobado` (oculta `rechazado` e `inactivo`).
    listarResidentesDeLote(loteSeleccionado)
      .then((data) => {
        if (!cancelado) setResidentes(data);
      })
      .catch(() => {
        if (!cancelado) setResidentes([]);
      });
    // Documentos y avisos del barrio (Sprint 12 S12-09). Tambien
    // best-effort: si fallan, listas vacias.
    listarDocumentosVecino()
      .then((data) => {
        if (!cancelado) setDocumentos(data);
      })
      .catch(() => {
        if (!cancelado) setDocumentos([]);
      });
    listarAvisosVecino()
      .then((data) => {
        if (!cancelado) setAvisos(data);
      })
      .catch(() => {
        if (!cancelado) setAvisos([]);
      });
    return () => {
      cancelado = true;
    };
  }, [loteSeleccionado]);

  if (!me) return null;

  if (casas.length === 0) {
    return (
      <div className="px-4 md:px-8 py-12">
        <h2 className="text-2xl font-medium text-text-primary mb-3">Inicio</h2>
        <p className="text-base text-text-secondary">
          No tienes una casa asignada en el portal. Si crees que es un error, contacta a la Comisión.
        </p>
      </div>
    );
  }

  if (loading) return <SkeletonDashboard />;

  if (error) {
    return (
      <div className="px-4 md:px-8 py-12">
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-4 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      </div>
    );
  }

  if (!estadoCuenta) return null;

  return (
    <>
      <VecinoDashboard
        me={me}
        lastSignInAt={lastSignInAt}
        estado={estadoCuenta}
        vehiculos={vehiculos}
        residentes={residentes}
        documentos={documentos}
        avisos={avisos}
        tenenciasMultiples={casas.length > 1}
        loteSeleccionado={loteSeleccionado}
        onLoteChange={setLoteSeleccionado}
        tenencias={casas}
      />
    </>
  );
}

// Re-export para tests que necesitan los helpers puros.
export { alertaDesdeAviso, ultimosMovimientos };
// Re-export del subcomponente self-contained para previews dev-only (S14-07).
// El componente toma todo via props, no llama hooks de auth ni APIs — seguro
// de instanciar fuera del flujo autenticado.
export { VecinoDashboard };
export type { VecinoDashboardProps };
